// Mock API response payloads used when VUE_APP_MOCK_MODE=true
//
// Customer-profile demo scenarios (all-green / low-data / not-installed /
// expired) live in ./scenarios - see scenarios/index.ts to switch which one
// mock mode serves.

const makeBalance = (name: string, country: string, sizeGb: number, remainingGb: number, activatedAt: string | null, expiresAt: string) => ({
    activatedAt,
    expiresAt,
    activationMode: "NOW" as const,
    name,
    size: { sizeValue: sizeGb, sizeUnit: "GB" },
    availableBalance: { sizeValue: remainingGb, sizeUnit: "GB" },
    validitySize: 30,
    validityUnit: "days",
    countrySet: country,
    extendedDataPolicy: null
});

export const MOCK_SOLD_ITEMS = [
    {
        uid: "sale-001",
        metatag: "customer-alice@example.com",
        salesDate: "2025-06-20T14:22:00.000Z",
        salesChannel: "API Integration",
        salesChannelTypeCode: "API",
        activationType: "DATA_PLAN",
        purchasePrice: { priceValue: 7.49, currencyCode: "USD" },
        retailPrice:   { priceValue: 9.99, currencyCode: "USD" },
        balance: makeBalance("Europe 5GB / 30 days", "DE", 5, 5, null, "2025-07-20T14:22:00.000Z")
    },
    {
        uid: "sale-002",
        metatag: "customer-bob@example.com",
        salesDate: "2025-06-19T09:05:00.000Z",
        salesChannel: "Shopify Store",
        salesChannelTypeCode: "SHOP",
        activationType: "DATA_PLAN",
        purchasePrice: { priceValue: 11.99, currencyCode: "USD" },
        retailPrice:   { priceValue: 14.99, currencyCode: "USD" },
        balance: makeBalance("USA 10GB / 30 days", "US", 10, 10, null, "2025-07-19T09:05:00.000Z")
    },
    {
        uid: "sale-003",
        metatag: "customer-carol@example.com",
        salesDate: "2025-06-17T17:45:00.000Z",
        salesChannel: "Point of Sales Store",
        salesChannelTypeCode: "POS",
        activationType: "TOP_UP",
        purchasePrice: { priceValue: 3.99, currencyCode: "USD" },
        retailPrice:   { priceValue: 4.99, currencyCode: "USD" },
        balance: makeBalance("Asia Unlimited Daily", "asa", 0, 0, "2025-06-17T18:00:00.000Z", "2025-07-17T18:00:00.000Z")
    },
    {
        uid: "sale-004",
        metatag: "customer-dave@example.com",
        salesDate: "2025-06-15T11:30:00.000Z",
        salesChannel: "API Integration",
        salesChannelTypeCode: "API",
        activationType: "DATA_PLAN",
        purchasePrice: { priceValue: 15.99, currencyCode: "USD" },
        retailPrice:   { priceValue: 19.99, currencyCode: "USD" },
        balance: makeBalance("Worldwide Max 20GB", "www", 20, 20, null, "2025-07-15T11:30:00.000Z")
    },
    {
        uid: "sale-005",
        metatag: "customer-eve@example.com",
        salesDate: "2025-06-12T08:10:00.000Z",
        salesChannel: "Shopify Store",
        salesChannelTypeCode: "SHOP",
        activationType: "DATA_PLAN",
        purchasePrice: { priceValue: 5.49, currencyCode: "USD" },
        retailPrice:   { priceValue: 7.49, currencyCode: "USD" },
        balance: makeBalance("Europe 3GB / 14 days", "FR", 3, 3, null, "2025-06-26T08:10:00.000Z")
    }
];

export const MOCK_CHART_CANDLES = {
    showIntroMessage: false,
    totalRevenue: {
        changeRate: 0.18,
        dataSet: [
            { currencyCode: "USD", timestamp: "2025-05-22T00:00:00.000Z", value: 42.50 },
            { currencyCode: "USD", timestamp: "2025-05-29T00:00:00.000Z", value: 67.00 },
            { currencyCode: "USD", timestamp: "2025-06-05T00:00:00.000Z", value: 53.75 },
            { currencyCode: "USD", timestamp: "2025-06-12T00:00:00.000Z", value: 89.25 },
            { currencyCode: "USD", timestamp: "2025-06-19T00:00:00.000Z", value: 110.48 }
        ]
    },
    revenuePerItem: [
        {
            inventoryItemId: "item-eu-5gb",
            inventoryItemName: "Europe 5GB / 30 days",
            revenue: {
                changeRate: 0.12,
                dataSet: [
                    { currencyCode: "USD", timestamp: "2025-05-22T00:00:00.000Z", value: 19.98 },
                    { currencyCode: "USD", timestamp: "2025-05-29T00:00:00.000Z", value: 29.97 },
                    { currencyCode: "USD", timestamp: "2025-06-05T00:00:00.000Z", value: 19.98 },
                    { currencyCode: "USD", timestamp: "2025-06-12T00:00:00.000Z", value: 39.96 },
                    { currencyCode: "USD", timestamp: "2025-06-19T00:00:00.000Z", value: 49.95 }
                ]
            }
        }
    ]
};

export const MOCK_ACCOUNT = {
    id: "mock-account-id",
    login: "demo@example.com",
    firstName: "Jane",
    lastName: "Demo",
    email: "demo@example.com",
    imageUrl: "",
    activated: true,
    langKey: "en",
    createdBy: "system",
    createdDate: "2024-01-01T00:00:00.000Z",
    lastModifiedBy: "system",
    lastModifiedDate: "2024-01-01T00:00:00.000Z",
    authorities: ["ROLE_RESELLER_ADMIN"],
    agentId: "mock-agent-id",
    customerEmail: "demo@example.com",
    companyName: "Demo Store",
    realmName: "mock-realm"
};

export const MOCK_USER = {
    firstName: "Jane",
    lastName: "Demo",
    email: "demo@example.com",
    companyName: "Demo Store",
    taxId: "DE123456789",
    addresses: []
};

export const MOCK_JOINER_BONUS = {
    showToUser: false,
    amount: 0,
    name: ""
};

export const MOCK_CREDIT_INFOS = {
    balance: { value: 250.00, currency: "USD" },
    external: null
};

export const MOCK_LEGAL_CONTENT = {
    userNeedsToAccept: false,
    userAccepted: true,
    currentVersion: "1.0",
    timestamp: "2024-01-01T00:00:00.000Z"
};
